import React, { useEffect, useState } from "react";
import {
    Box, Typography, Paper, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Button, TextField, 
    Stack, Chip, Dialog, DialogTitle, DialogContent, 
    DialogActions, MenuItem, InputAdornment, FormControlLabel, Switch, Divider, IconButton, Tooltip
} from "@mui/material";
import { Search, History, AccountBalance, InfoOutlined, CheckCircle } from "@mui/icons-material";
import { collection, getDocs, doc, query, where, writeBatch, increment } from "firebase/firestore";
import { db } from "../../firebase";
import { format } from "date-fns";

function Monthlypayments() {
    const [staffList, setStaffList] = useState([]);
    const [attendanceData, setAttendanceData] = useState([]);
    const [advanceData, setAdvanceData] = useState([]);
    const [banks, setBanks] = useState([]);
    const [paidRecords, setPaidRecords] = useState({}); // Stores full payment objects
    const [loading, setLoading] = useState(false);
    
    const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));
    const [searchTerm, setSearchTerm] = useState("");
    
    // Dialog States
    const [payDialogOpen, setPayDialogOpen] = useState(false);
    const [activeStaff, setActiveStaff] = useState(null);
    const [deductAdvance, setDeductAdvance] = useState(true);
    const [paymentDetails, setPaymentDetails] = useState({
        date: format(new Date(), "yyyy-MM-dd"),
        amount: 0,
        bankId: "",
        payMode: "UPI",
        description: ""
    });

    useEffect(() => { loadData(); }, [selectedMonth]);

    const loadData = async () => {
        setLoading(true);
        try {
            const staffSnap = await getDocs(collection(db, "salaryAllocations"));
            setStaffList(staffSnap.docs.map(d => ({ id: d.id, ...d.data() })));

            const attendanceSnap = await getDocs(collection(db, "staffattendance"));
            setAttendanceData(attendanceSnap.docs.map(d => d.data()).filter(a => a.date.startsWith(selectedMonth)));

            // Fetch Pending Advances (to deduct)
            const advanceSnap = await getDocs(query(collection(db, "staffadvances"), where("status", "==", "Pending")));
            setAdvanceData(advanceSnap.docs.map(d => ({ id: d.id, ...d.data() })));

            const bankSnap = await getDocs(collection(db, "banks"));
            setBanks(bankSnap.docs.map(d => ({ id: d.id, ...d.data() })));

            // Fetch Payment Records for the selected month to check "Paid" status
            const paymentsSnap = await getDocs(query(collection(db, "monthlyPayments"), where("month", "==", selectedMonth)));
            const paidMap = {};
            paymentsSnap.forEach(doc => { paidMap[doc.data().staffId] = doc.data(); });
            setPaidRecords(paidMap);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const calculateStats = (staff) => {
        const logs = attendanceData.filter(a => a.staffId === staff.id);
        const p = logs.filter(l => l.status === "Present" || l.status === "Late Mark").length;
        const h = logs.filter(l => l.status === "Half Day").length;
        
        // Sum only Pending advances
        const pendingAdvanceSum = advanceData
            .filter(adv => adv.staffId === staff.staffId)
            .reduce((sum, item) => sum + (Number(item.payment) || 0), 0);
        console.log(pendingAdvanceSum);
        
        const base = Number(staff.totalSalary) || 0;
        const earned = Math.round((p * (base / 30)) + (h * 0.5 * (base / 30)));
        
        return { p, h, earned, pendingAdvanceSum };
    };

    const handleFinalPay = async () => {
        if (paymentDetails.payMode !== "Cash" && !paymentDetails.bankId) return alert("Select a bank");

        const batch = writeBatch(db);
        const paymentId = `${activeStaff.id}_${selectedMonth}`;

        // 1. Save Monthly Payment
        const payRef = doc(db, "monthlyPayments", paymentId);
        batch.set(payRef, {
            staffId: activeStaff.id,
            staffName: activeStaff.staffName,
            month: selectedMonth,
            ...paymentDetails,
            advanceDeducted: deductAdvance,
            createdAt: new Date().toISOString()
        });

        // 2. Mark Advances as Paid
        if (deductAdvance) {
            const staffAdvances = advanceData.filter(a => a.staffId === activeStaff.id);
            
            staffAdvances.forEach(adv => {
                const advRef = doc(db, "staffadvances", adv.id);
                batch.update(advRef, { status: "Paid", deductionMonth: selectedMonth });
            });
        }

        // 3. Deduct from Bank
        if (paymentDetails.payMode !== "Cash") {
            const bankRef = doc(db, "banks", paymentDetails.bankId);
            batch.update(bankRef, { balance: increment(-Number(paymentDetails.amount)) });
        }

        await batch.commit();
        setPayDialogOpen(false);
        loadData();
    };

    return (
        <Box p={3} sx={{ bgcolor: '#f9f9f9', minHeight: '100vh' }}>
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Typography variant="h6" fontWeight="bold">Monthly Salary Processing</Typography>
                    <Stack direction="row" spacing={2}>
                        <TextField 
                            type="month" 
                            size="small" 
                            value={selectedMonth} 
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            label="Select Month"
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField 
                            placeholder="Search Staff..." 
                            size="small" 
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'gray' }} /> }}
                        />
                    </Stack>
                </Stack>
            </Paper>

            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#eee' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Staff Details</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Attendance (P|H)</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Earned Amount</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Pending Advance</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {staffList.filter(s => s.staffName.toLowerCase().includes(searchTerm.toLowerCase())).map((staff) => {
                            const { p, h, earned, pendingAdvanceSum } = calculateStats(staff);
                            const paymentRecord = paidRecords[staff.id];
                            const isPaid = !!paymentRecord;

                            return (
                                <TableRow key={staff.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{staff.staffName}</Typography>
                                        <Typography variant="caption" color="textSecondary">Base: ₹{staff.totalSalary}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip label={`${p} P | ${h} H`} size="small" variant="outlined" color="primary" />
                                    </TableCell>
                                    <TableCell align="center">₹{earned}</TableCell>
                                    <TableCell align="center">
                                        <Typography color={pendingAdvanceSum > 0 ? "error" : "inherit"} fontWeight="bold">
                                            ₹{pendingAdvanceSum}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        {isPaid ? (
                                            <Tooltip title={`Paid via ${paymentRecord.payMode} on ${paymentRecord.date}`}>
                                                <Chip icon={<CheckCircle />} label="Paid" color="success" size="small" />
                                            </Tooltip>
                                        ) : (
                                            <Chip label="Unpaid" variant="outlined" size="small" />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {isPaid ? (
                                            <Typography variant="body2" fontWeight="bold">₹{paymentRecord.amount}</Typography>
                                        ) : (
                                            <Button 
                                                variant="contained" 
                                                size="small" 
                                                onClick={() => {
                                                    setActiveStaff(staff);
                                                    const net = deductAdvance ? (earned - pendingAdvanceSum) : earned;
                                                    setPaymentDetails({ ...paymentDetails, amount: net });
                                                    setPayDialogOpen(true);
                                                }}
                                            >
                                                Process
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Payment Dialog */}
            <Dialog open={payDialogOpen} onClose={() => setPayDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 'bold' }}>Salary Payment: {activeStaff?.staffName}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Box display="flex" justifyContent="space-between" p={1.5} bgcolor="#f0f7ff" borderRadius={1}>
                            <Typography variant="body2">Deduct Pending Advance?</Typography>
                            <Switch 
                                checked={deductAdvance} 
                                size="small"
                                onChange={(e) => {
                                    setDeductAdvance(e.target.checked);
                                    const { earned, pendingAdvanceSum } = calculateStats(activeStaff);
                                    setPaymentDetails({
                                        ...paymentDetails, 
                                        amount: e.target.checked ? (earned - pendingAdvanceSum) : earned
                                    });
                                }} 
                            />
                        </Box>

                        <TextField 
                            label="Payable Amount" 
                            type="number" 
                            fullWidth 
                            value={paymentDetails.amount} 
                            onChange={(e) => setPaymentDetails({...paymentDetails, amount: e.target.value})} 
                        />

                        <TextField select label="Pay Mode" fullWidth size="small" value={paymentDetails.payMode} onChange={(e) => setPaymentDetails({...paymentDetails, payMode: e.target.value})}>
                            {["Cash", "UPI", "Bank Transfer"].map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                        </TextField>

                        {paymentDetails.payMode !== "Cash" && (
                            <TextField select label="Select Bank" fullWidth size="small" value={paymentDetails.bankId} onChange={(e) => setPaymentDetails({...paymentDetails, bankId: e.target.value})}>
                                {banks.map(b => <MenuItem key={b.id} value={b.id}>{b.bankname} </MenuItem>)}
                            </TextField>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setPayDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="success" onClick={handleFinalPay} startIcon={<CheckCircle />}>Confirm Payment</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Monthlypayments;